import { useEffect, useState } from "react";
import logo from "../../assets/img/spire360-by-wellinks-logo.png";
import { Spinner } from "react-bootstrap";
import "../../assets/scss/custom/_carePlan.scss";
import {
  CarePlanAddendumDto,
  CarePlanDto,
  GridDataResponse,
  InternalCareTeamMembers,
  PatientPersonalInfo,
  Contracts,
  PatientMonthlyBillingCode,
  PatientMonthlyBillableEvent,
} from "patients-experience-client";
import { Lookup } from "../../interfaces/allergy";
import {
  formatDate,
  formatDateTime,
  formatDateTimeShort,
  formatDateToMMDDYYYY,
  getAge,
} from "../../helper/dateFormatter";
import {
  PatientGoalComment,
  PatientInterventionComment,
} from "pgi-experience-client";
import ReactQuill from "react-quill";
import { WorkflowState } from "common-util-experience-client";
import {
  TimeZoneConstants,
  WorkflowConstants,
  WorkflowStateConstants,
} from "../../helper/WorkflowConstants";
import { useParams, useSearchParams } from "react-router-dom";
import {
  createCarePlanApi,
  createPatientApi,
  createPgiApi,
  createWorkflowApi,
  createCommonUtilsApi,
  createGridApi,
} from "../../api-helpers/care-plan/CarePlanAPis";
// import { createGridApi } from "../../api-helpers/GridApiHelper";
import {
  PatientGoals,
  PatientIntervention,
  PatientPgiMapResponse,
} from "grid-experience-client";
import { formatDateTimeToLocal } from "../schedule/dateFormat";
import { DateTime } from "luxon";
import Lookups from "../../constants/Lookups";
// import { createCommonUtilsApi } from "../../api-helpers/CommonUtilsApiHelper";

// interface ViewCarePlanProps {
//   onHide: () => void;
//   selectedCarePlan: CarePlanDto;
//   reloadGrid?: () => void
//   reloadCarePlan?: (id: string) => void
// }

interface LoadingDataState {
  pgis: boolean;
  patientConditions: boolean;
  patientMedications: boolean;
  addendum: boolean;
  patientInfo: boolean;
  partnerName: boolean;
  internalCareTeam: boolean;
}

const DownloadCarePlan = () => {
  // UseState Variables
  const [workflowStates, setWorkflowStates] = useState<WorkflowState[] | []>(
    [],
  );
  const [selectedCarePlanDetails, setSelectedCarePlanDetails] = useState(
    {} as CarePlanDto,
  );
  const [addendumDetails, setAddendumDetails] = useState<
    CarePlanAddendumDto[] | []
  >([]);
  const [patientInfo, setPatientInfo] = useState({} as PatientPersonalInfo);
  const [patientConditions, setPatientConditions] =
    useState<GridDataResponse>();
  const [patientMedications, setPatientMedications] =
    useState<GridDataResponse>();
  const [patientPgis, setPatientPgis] = useState<PatientPgiMapResponse[] | []>(
    [],
  );
  const [pgiGoalComments, setPgiGoalComments] = useState<
    Map<string, PatientGoalComment[]>
  >(new Map());
  const [pgiInterventionComments, setPgiInterventionComments] = useState<
    Map<string, PatientInterventionComment[]>
  >(new Map());
  const [patientMonthlyBillableEvents, setPatientMonthlyBillableEvents] =
    useState<PatientMonthlyBillableEvent[]>([]);
  const [patientMonthlyBillingCodes, setPatientMonthlyBillingCodes] = useState<
    PatientMonthlyBillingCode[]
  >([]);
  const [coachAddendum, setCoachAddendum] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [loadingData, setLoadingData] = useState<LoadingDataState>({
    pgis: true,
    patientConditions: true,
    patientMedications: true,
    addendum: true,
    patientInfo: true,
    partnerName: true,
    internalCareTeam: true,
  });
  const [editContent, setEditContent] = useState(false);
  const [patientContract, setPatientContract] = useState<Contracts>();
  const [internalCareTeam, setInternalCareTeam] =
    useState<InternalCareTeamMembers>();
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get("token");
  const billingMonth = searchParams.get("billing_month");

  // APIs
  const carePlanApi = createCarePlanApi();
  const patientApi = createPatientApi();
  const pgiApi = createPgiApi();
  const workflowApi = createWorkflowApi();
  const gridApi = createGridApi();
  const lookupApi = createCommonUtilsApi();
  const { carePlanId } = useParams();

  const badgeStatus = {
    "Needs Action": "info",
    "In-Progress": "rescheduled",
    Done: "completed",
  };

  const getPatientPersonalInfo = (carePlan: CarePlanDto) => {
    patientApi
      .patientsControllerGetPatientPersonalInfo(carePlan.patientId)
      .then((response) => {
        setPatientInfo(response.data);
      })
      .catch((error) => {
        console.error("Error fetching patient personal info:", error);
      })
      .finally(() => {
        setLoadingData((prev) => ({ ...prev, patientInfo: false }));
      });
  };

  const getPatientContract = async () => {
    try {
      const response = await patientApi.patientsControllerGetPatientContracts(
        patientInfo.patientId,
      );
      if (response.data.length) {
        setPatientContract(response.data[0]);
      }
    } catch (error) {
      console.log("Error in loading patient contract ");
    } finally {
      setLoadingData((prev) => ({ ...prev, partnerName: false }));
    }
  };

  const getInternalCareTeam = async () => {
    try {
      const internalCareTeamResponse =
        await patientApi.patientsControllerGetInternalCareTeam(
          patientInfo.patientId,
        );
      const internalCareTeam = internalCareTeamResponse.data;
      setInternalCareTeam(internalCareTeam);
    } catch (error) {
      console.error("Error in fetching Internal Care Team");
    } finally {
      setLoadingData((prev) => ({ ...prev, internalCareTeam: false }));
    }
  };

  const getAddendumDetails = async (carePlan: CarePlanDto) => {
    try {
      const response = await carePlanApi.carePlanControllerGetCarePlanAddedums(
        carePlan?.id,
      );
      setAddendumDetails(response.data);
      if (isReviewPending()) {
        setCoachAddendum("");
      } else {
        setCoachAddendum(response?.data[0]?.addendum || "");
      }
      setEditContent(false);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingData((prev) => ({ ...prev, addendum: false }));
    }
  };

  const isReviewPending = (): boolean => {
    return selectedCarePlanDetails.status === "Review Pending" ? true : false;
  };

  const getWorkFlowStates = async (): Promise<WorkflowState[]> => {
    let states: WorkflowState[] = [];
    try {
      const statesResponse =
        await workflowApi.workflowControllerGetStatesForWorkflow(
          WorkflowConstants.carePlan,
        );
      states = [...statesResponse.data];
      setWorkflowStates(states);
    } catch (error) {
      console.log(error);
      return [] as WorkflowState[];
    }
    return states;
  };

  const loadAllData = (carePlan: CarePlanDto) => {
    if (carePlan.isDraft) {
      setLoadingData({
        pgis: true,
        patientConditions: true,
        patientMedications: true,
        addendum: true,
        patientInfo: true,
        partnerName: true,
        internalCareTeam: true,
      });
      getPgis(carePlan.patientId);
      getPatientConditions(carePlan.patientId);
      getPatientMedications(carePlan.patientId);
    } else {
      setLoadingData({
        pgis: false,
        patientConditions: false,
        patientMedications: false,
        addendum: true,
        patientInfo: true,
        partnerName: true,
        internalCareTeam: true,
      });
      setPatientPgis(carePlan.pgi?.pgis);
      setPatientConditions(carePlan.conditions?.conditions);
      setPatientMedications(carePlan.medications?.medications);
    }
    getWorkFlowStates();
    getAddendumDetails(carePlan);
    getPatientPersonalInfo(carePlan);
  };

  useEffect(() => {
    if (selectedCarePlanDetails.patientId) {
      getPgis(selectedCarePlanDetails.patientId);
    }
  }, [selectedCarePlanDetails]);

  useEffect(() => {
    if (selectedCarePlanDetails.patientId && billingMonth) {
      getPatientMonthlyBillingCodes(
        selectedCarePlanDetails.patientId,
        billingMonth,
      );
      getPatientMonthlyBillableEvents(
        selectedCarePlanDetails.patientId,
        billingMonth,
      );
    }
  }, [selectedCarePlanDetails, billingMonth]);

  useEffect(() => {
    if (carePlanId) {
      getCarePlanDetailsById(carePlanId);
    }
  }, [carePlanId]);

  useEffect(() => {
    if (patientInfo?.patientId) {
      getPatientContract();
      getInternalCareTeam();
    }
  }, [patientInfo]);

  const getCarePlanDetailsById = async (carePlanId: string) => {
    const carePlan =
      await carePlanApi.carePlanControllerGetCarePlanDetails(carePlanId);

    setSelectedCarePlanDetails(carePlan.data);
    loadAllData(carePlan.data);
  };

  const getPatientMonthlyBillingCodes = async (
    patientId: string,
    billingMonth: string,
  ) => {
    let response: PatientMonthlyBillingCode[] = [];
    try {
      response = (
        await patientApi.patientsControllerGetPatientMonthlyBillingCodes(
          patientId,
          billingMonth,
        )
      ).data;

      setPatientMonthlyBillingCodes(response);
    } catch (error) {
      console.log(error);
    }
  };

  const getPatientMonthlyBillableEvents = async (
    patientId: string,
    billingMonth: string,
  ) => {
    let response: PatientMonthlyBillableEvent[] = [];

    try {
      response = (
        await patientApi.patientsControllerGetPatientMonthlyBillableEvents(
          patientId,
          billingMonth,
        )
      ).data;

      setPatientMonthlyBillableEvents(response);
    } catch (error) {
      console.log(error);
    }
  };

  const getPgis = async (patientId: string) => {
    try {
      const pgiResponse =
        await gridApi.gridControllerGetPatientPgiMap(patientId);
      const pgiData = pgiResponse.data;

      if (pgiData) {
        const structuredPGIs = pgiData?.filter(
          (pgi: PatientPgiMapResponse) => pgi.goal?.length > 0,
        );
        setPatientPgis(structuredPGIs);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingData((prev) => ({ ...prev, pgis: false }));
    }
  };

  const getPatientConditions = async (patientId: string) => {
    try {
      const activeConditionId = await getActiveConditionStatus();
      const reviewedConditionId = await getReviewedStatus(
        Lookups.CONDITION_RECONCILIATION_STATUS,
      );

      const conditionsPayload = {
        filters: [
          {
            propertyName: "patientId",
            operator: "equals",
            propertyValue: patientId,
          },
          {
            propertyName: "status",
            operator: "in",
            propertyValue: activeConditionId,
          },
          {
            propertyName: "conditionRecStatusId",
            operator: "in",
            propertyValue: reviewedConditionId,
          },
        ],
        sortCriteria: [],
        startRow: 0,
        endRow: 100,
      };

      const response =
        await patientApi.patientsControllerGetConditionGridData(
          conditionsPayload,
        );
      setPatientConditions(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingData((prev) => ({ ...prev, patientConditions: false }));
    }
  };

  const getActiveConditionStatus = async (): Promise<string[]> => {
    let response,
      conditionStatus: Lookup[] = [];
    try {
      response = await lookupApi.lookupControllerFindAll(
        Lookups.CONDITION_STATUS,
      );
      conditionStatus = response.data.filter(
        (lookup: Lookup) => lookup.name?.toLowerCase() === "active",
      );
    } catch (error) {
      console.log(error);
    }
    return conditionStatus.length > 0 ? [conditionStatus[0].id] : [];
  };

  const getActiveMedicationStatus = async (): Promise<string[]> => {
    let response,
      medicationStatus: Lookup[] = [];
    try {
      response = await lookupApi.lookupControllerFindAll(
        Lookups.MEDICATION_STATUS,
      );
      medicationStatus = response.data.filter(
        (lookup: Lookup) => lookup.name?.toLowerCase() === "active",
      );
    } catch (error) {
      console.log(error);
    }
    return medicationStatus.length > 0 ? [medicationStatus[0].id] : [];
  };

  const getReviewedStatus = async (source: string): Promise<string[]> => {
    let response,
      reviewedStatus: Lookup[] = [];
    try {
      response = await lookupApi.lookupControllerFindAll(source);
      reviewedStatus = response.data.filter(
        (lookup: Lookup) => lookup.name?.toLowerCase() === "reviewed",
      );
    } catch (error) {
      console.log(error);
    }
    return reviewedStatus.length > 0 ? [reviewedStatus[0].id] : [];
  };

  const getPatientMedications = async (patientId: string) => {
    try {
      const activeMedicationId = await getActiveMedicationStatus();
      const reviewedMedicationId = await getReviewedStatus(
        Lookups.MEDICATION_RECONCILIATION_STATUS,
      );

      const request = {
        filters: [
          {
            propertyName: "patientId",
            operator: "equals",
            propertyValue: patientId,
          },
          {
            propertyName: "statusId",
            operator: "in",
            propertyValue: activeMedicationId,
          },
          {
            propertyName: "medRecStatusId",
            operator: "in",
            propertyValue: reviewedMedicationId,
          },
        ],
        sortCriteria: [],
        startRow: 0,
        endRow: 100,
      };

      const response =
        await gridApi.gridControllerGetMedicationGridData(request);
      setPatientMedications(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingData((prev) => ({ ...prev, patientMedications: false }));
    }
  };

  useEffect(() => {
    const isFullyLoaded = Object.values(loadingData).every(
      (value) => value === false,
    );
    if (isFullyLoaded) {
      // All data is loaded
      setLoading(false);
    }
  }, [loadingData]);

  const formatDateToTimeZone = (date: string, timeZone: string) => {
    debugger;
    const dateObject = DateTime.fromJSDate(
      new Date(DateTime.fromISO(date, { zone: "utc" }).toISO()!),
    ).setZone(timeZone);
    return dateObject.toFormat("MM/dd/yyyy HH:mm a") + " (EST)";
  };

  return (
    <>
      {loading ? (
        <div className="d-flex justify-content-center mt-5">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="download-care-plan-container font-century-gothic">
            <main className="row g-8" id="view-care-plan-container">
              <div
                className={`col-12 position-relative ${
                  editContent || isReviewPending() ? "col-md-9" : ""
                }`}
              >
                {selectedCarePlanDetails?.isRejected && (
                  <div className="rejected" role="alert">
                    <h6 className="mb-2 fs-16 text-black">
                      Rejected By {selectedCarePlanDetails?.reviewedBy?.name}
                    </h6>
                    <div className="gray-800">
                      {selectedCarePlanDetails?.rejectionReason}
                    </div>
                  </div>
                )}

                <img
                  src={logo}
                  className="min-w-5 position-absolute spire-logo"
                  alt="wellinks"
                  width={200}
                />

                {/* Personalized Care Plan  */}
                <h4 className="mt-10">Personalized Care Plan</h4>
                <div className="d-flex col-12 flex-wrap">
                  <div className="col-6">
                    {/* Patient Information */}
                    <h5>Patient Information</h5>
                    <div className="d-flex col-12 flex-wrap">
                      <span className="col-6">
                        <strong>Name:</strong> {patientInfo.firstName}{" "}
                        {patientInfo.lastName}
                      </span>
                      <span className="col-6">
                        <strong>DOB:</strong> {formatDate(patientInfo.dob)}
                      </span>
                      <span className="col-6">
                        <strong>Age:</strong> {getAge(patientInfo.dob)}
                      </span>
                      <span className="col-6">
                        <strong>Partner:</strong> {patientContract?.partnerName}
                      </span>
                    </div>
                  </div>

                  {/* Clinician Information */}
                  <div className="col-6 mt-2">
                    {internalCareTeam?.primaryCoach?.name && (
                      <div className="col-12">
                        <strong>Clinician:</strong>{" "}
                        {internalCareTeam?.primaryCoach?.name}
                      </div>
                    )}
                    {selectedCarePlanDetails?.reviewedBy?.name && (
                      <div className="col-12">
                        <strong>Reviewed By:</strong>{" "}
                        {selectedCarePlanDetails?.reviewedBy?.name}
                      </div>
                    )}
                    {selectedCarePlanDetails?.reviewedOn && (
                      <div className="col-12">
                        {!selectedCarePlanDetails?.reviewedBy?.name ? (
                          <div>
                            <strong>Reviewed On:</strong>{" "}
                            {formatDateTime(
                              selectedCarePlanDetails?.reviewedOn,
                            ) || "-"}
                          </div>
                        ) : (
                          <>
                            <strong>Electronically Signed On:</strong>{" "}
                            {formatDateTime(
                              selectedCarePlanDetails?.reviewedOn,
                            ) || "-"}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <br />
                <br />
                <br />
                {patientContract?.carePlanBillingCodes &&
                  billingMonth &&
                  (patientMonthlyBillableEvents.length > 0 ||
                    patientMonthlyBillingCodes.length > 0) && (
                    <div
                      style={{ pageBreakAfter: "always" }}
                      className="print-page-break"
                    >
                      {patientMonthlyBillingCodes.length > 0 && (
                        <>
                          <h4 className="mt-8">Monthly Billing Codes</h4>
                          <div className="bg-white rounded p-0 fs-14">
                            <table
                              className="table table-fixed table-sm mb-0"
                              style={{ width: "65%" }}
                            >
                              <thead>
                                <tr>
                                  <th
                                    className="p-2 border-end bg-teal-light"
                                    style={{ width: "16.33%" }}
                                  >
                                    Billing Code
                                  </th>
                                  <th
                                    className="p-2 border-end bg-teal-light"
                                    style={{ width: "16.33%" }}
                                  >
                                    Service Start Date
                                  </th>
                                  <th
                                    className="p-2 border-end bg-teal-light"
                                    style={{ width: "16.33%" }}
                                  >
                                    Note
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {patientMonthlyBillingCodes.map((bc) => {
                                  return (
                                    <tr key={bc.id}>
                                      <td className="p-2 text-muted">
                                        {bc.billingCode}
                                      </td>
                                      <td className="p-2 text-muted">
                                        {formatDateToMMDDYYYY(
                                          new Date(bc.codeStartDate),
                                        )}
                                      </td>
                                      <td className="p-2 text-muted">
                                        {bc.notes}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                      {patientMonthlyBillableEvents.length > 0 && (
                        <div>
                          <h4 className="mt-8">Monthly Billable Events</h4>
                          <div className="bg-white rounded p-0 fs-14">
                            <table className="table table-fixed table-sm mb-0">
                              <thead>
                                <tr>
                                  <th
                                    className="p-2 border-end bg-teal-light"
                                    style={{ width: "16.67%" }}
                                  >
                                    Start Time
                                  </th>
                                  <th
                                    className="p-2 border-end bg-teal-light"
                                    style={{ width: "16.67%" }}
                                  >
                                    End Time
                                  </th>
                                  <th
                                    className="p-2 border-end bg-teal-light"
                                    style={{ width: "16.67%" }}
                                  >
                                    Type
                                  </th>
                                  <th
                                    className="p-2 border-end bg-teal-light"
                                    style={{ width: "16.67%" }}
                                  >
                                    Sub-Type
                                  </th>
                                  <th
                                    className="p-2 border-end bg-teal-light"
                                    style={{ width: "16.67%" }}
                                  >
                                    Mode
                                  </th>
                                  <th
                                    className="p-2 border-end bg-teal-light"
                                    style={{ width: "16.67%" }}
                                  >
                                    Duration (min)
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {patientMonthlyBillableEvents.map(
                                  (billableEvent) => {
                                    return (
                                      <tr key={billableEvent.id}>
                                        <td className="p-2 text-muted">
                                          {formatDateTimeShort(
                                            billableEvent.startDate.toString(),
                                          )}
                                        </td>
                                        <td className="p-2 text-muted">
                                          {formatDateTimeShort(
                                            billableEvent.endDate.toString(),
                                          )}
                                        </td>
                                        <td className="p-2 text-muted">
                                          {billableEvent.billableType}
                                        </td>
                                        <td className="p-2 text-muted">
                                          {billableEvent.notes}
                                        </td>
                                        <td className="p-2 text-muted">
                                          {billableEvent.communicationMode}
                                        </td>
                                        <td className="p-2 text-muted">
                                          {billableEvent.serviceDurationMinutes}
                                        </td>
                                      </tr>
                                    );
                                  },
                                )}
                                <tr key="total">
                                  <td className="p-2 text-muted"></td>
                                  <td className="p-2 text-muted"></td>
                                  <td className="p-2 text-muted"></td>
                                  <td className="p-2 text-muted"></td>
                                  <td className="p-2 text-muted">
                                    <strong>Total</strong>
                                  </td>
                                  <td className="p-2 text-muted">
                                    <strong>
                                      {patientMonthlyBillableEvents.reduce(
                                        (total, event) =>
                                          total +
                                          (event.serviceDurationMinutes || 0),
                                        0,
                                      )}
                                    </strong>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                {
                  /* Patient Active Conditions */
                  patientConditions?.total ? (
                    <>
                      <br />

                      <div>
                        <h4 className="mt-4">Active Conditions</h4>
                        <div className="bg-white rounded p-0 fs-14">
                          <div className="d-flex bg-teal-light">
                            <strong className="w-20 p-3 border-end">
                              Code
                            </strong>
                            <strong className="w-80 p-3">Condition</strong>
                          </div>
                          <hr className="m-0" />
                          {patientConditions?.data?.map(
                            (condition: any, index: number) => (
                              <div
                                key={index}
                                className={`d-flex d-block border-bottom ${index % 2 === 1 ? "bg-light-subtle" : ""}`}
                              >
                                <span className="w-20 p-3 border-end">
                                  {condition.conditionName.split(":")[0]}
                                </span>
                                <span className="w-80 p-3">
                                  {condition.conditionName.split(":")[1]}
                                </span>
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    </>
                  ) : null
                }

                {
                  /* Patient Active Medications */
                  patientMedications?.total ? (
                    <>
                      <br />
                      <div>
                        <h4 className="mt-4">Active Medications</h4>
                        <div className="bg-white rounded p-0 fs-14">
                          <div className="d-flex bg-teal-light">
                            <strong className="w-35 p-3 border-end">
                              Name
                            </strong>
                            <strong className="w-65 p-3">Dosage</strong>
                          </div>
                          <hr className="m-0" />
                          {patientMedications?.data?.map(
                            (medication: any, index: number) => (
                              <div
                                key={index}
                                className={`d-flex d-block border-bottom ${index % 2 === 1 ? "bg-light-subtle" : ""}`}
                              >
                                <span className="w-35 p-3 border-end">
                                  {medication.medicationName}
                                </span>
                                <span className="w-65 p-3">
                                  {medication.dosageRecommended}
                                </span>
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    </>
                  ) : null
                }

                <br />
                {
                  /* Patient Active Problems, Goals and Interventions */
                  patientPgis?.length ? (
                    <div>
                      <h4 className="mt-4">
                        Active Problems, Goals & Interventions
                      </h4>
                      <div className="bg-white rounded p-0 fs-14">
                        {patientPgis?.map(
                          (
                            pgi: PatientPgiMapResponse,
                            problemIndex: number,
                          ) => (
                            <>
                              <span className="fs-15 mb-2">
                                {problemIndex + 1} {". "}
                                <strong>PROBLEM: </strong>
                                {pgi?.problem?.problem}
                              </span>

                              {pgi.goal?.map(
                                (pgiGoal: PatientGoals, goalIndex: number) => (
                                  <>
                                    <div
                                      key={goalIndex}
                                      className="d-flex d-block border-bottom bg-teal-light"
                                    >
                                      <span className="w-20 border-end p-3">{`Goal ${
                                        goalIndex + 1
                                      }`}</span>
                                      <div className="w-90 d-flex justify-content-between p-3">
                                        <span>{pgiGoal.goal?.goal} </span>
                                        <span
                                          className={`h-fit-content badge background-${
                                            badgeStatus[
                                              pgiGoal.status as keyof typeof badgeStatus
                                            ]
                                          }`}
                                        >
                                          {pgiGoal.status}
                                        </span>
                                      </div>
                                    </div>

                                    {pgiGoal.interventions?.map(
                                      (
                                        pgiIntervention: PatientIntervention,
                                        interventionIndex: number,
                                      ) => (
                                        <div
                                          key={interventionIndex}
                                          className="d-flex d-block border-bottom"
                                        >
                                          <span className="w-20 border-end p-3">{`Intervention ${
                                            interventionIndex + 1
                                          }`}</span>
                                          <div className="w-90 d-flex justify-content-between p-3">
                                            <span>
                                              {
                                                pgiIntervention.intervention
                                                  ?.intervention
                                              }
                                            </span>
                                            <span
                                              className={`h-fit-content badge background-${
                                                badgeStatus[
                                                  pgiIntervention.status as keyof typeof badgeStatus
                                                ]
                                              }`}
                                            >
                                              {pgiIntervention.status}
                                            </span>
                                          </div>
                                        </div>
                                      ),
                                    )}
                                  </>
                                ),
                              )}

                              <br />
                            </>
                          ),
                        )}
                      </div>
                    </div>
                  ) : null
                }

                <br />

                {
                  // Addendums
                  addendumDetails.length ? (
                    <div className="mt-4">
                      <h5 className="text-decoration-underline mt-6">
                        Addendum
                      </h5>
                      {addendumDetails.map((addendum, index) => (
                        <div className="ms-4 text-dark fs-13 pb-2" key={index}>
                          <div className="mt-3">
                            <div className="d-flex gap-2 justify-content-between">
                              <h6 className="fs-15">
                                By Coach{" "}
                                <span className="fw-normal">
                                  {"("}
                                  {addendum?.writtenByUser?.name || ""}
                                  {")"}
                                </span>
                              </h6>
                              <div>{formatDate(addendum.createdAt)}</div>
                            </div>
                          </div>
                          <ReactQuill
                            value={addendum.addendum || ""}
                            readOnly={true}
                            theme="bubble"
                            modules={{ toolbar: false }}
                          />
                        </div>
                      ))}
                    </div>
                  ) : null
                }
              </div>
            </main>
          </div>
        </>
      )}
    </>
  );
};

export default DownloadCarePlan;
